import { createBrowserRouter, Navigate } from 'react-router';
import { RequireAuth } from '@/auth/require-auth';
import { AppLayout } from '@/layouts/app-layout';
import { DropOffPage } from '@/routes/analytics/drop-off';
import { FunnelPage } from '@/routes/analytics/funnel';
import { JourneyDetailPage } from '@/routes/analytics/journey-detail';
import { JourneysPage } from '@/routes/analytics/journeys';
import { UserDetailPage } from '@/routes/users/detail';
import { UsersPage } from '@/routes/users/list';
import { WaitlistPage } from '@/routes/waitlist/list';
import { SignalsPage } from '@/routes/analytics/signals';
import { DashboardPage } from '@/routes/dashboard';
import { NotFoundPage } from '@/routes/not-found';
import { CharactersPage } from '@/routes/settings/characters';
import { LanguagesPage } from '@/routes/settings/languages';
import { LlmPage } from '@/routes/settings/llm';
import { HeadlinesPage } from '@/routes/settings/headlines';
import { InterestsPage } from '@/routes/settings/interests';
import { MotivationsPage } from '@/routes/settings/motivations';
import { SignInPage } from '@/routes/sign-in';

/**
 * Everything except /sign-in sits behind RequireAuth, so a new screen is
 * protected by default. Making a route public should be a deliberate act of
 * moving it out of that branch, not the result of forgetting to guard it.
 */
export const router = createBrowserRouter([
  {
    path: '/sign-in',
    element: <SignInPage />,
  },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <DashboardPage /> },
          { path: 'analytics', element: <FunnelPage /> },
          { path: 'analytics/drop-off', element: <DropOffPage /> },
          { path: 'analytics/signals', element: <SignalsPage /> },
          { path: 'analytics/journeys', element: <JourneysPage /> },
          {
            path: 'analytics/journeys/:journeyId',
            element: <JourneyDetailPage />,
          },
          // Settings lands on the first of its screens rather than carrying an
          // index of its own to
          // show. A redirect keeps the URL people will type working without
          // inventing a landing page that only lists its single child.
          { path: 'users', element: <UsersPage /> },
          // A sibling entry rather than a nested child, matching
          // analytics/journeys: there is no shared chrome between the list and
          // the detail page, so an Outlet would be a layout that renders
          // nothing.
          { path: 'users/:userId', element: <UserDetailPage /> },
          { path: 'waitlist', element: <WaitlistPage /> },
          {
            path: 'settings',
            element: <Navigate to="/settings/languages" replace />,
          },
          { path: 'settings/languages', element: <LanguagesPage /> },
          { path: 'settings/motivations', element: <MotivationsPage /> },
          { path: 'settings/headlines', element: <HeadlinesPage /> },
          { path: 'settings/interests', element: <InterestsPage /> },
          { path: 'settings/characters', element: <CharactersPage /> },
          { path: 'settings/llm', element: <LlmPage /> },
        ],
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
